import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid2";

import BucketApi from "api/bucketApi";

function PDFsCard({ proceso }) {
  const [pdfUrls, setPdfUrls] = useState([]);
  const [zipInfo, setZipInfo] = useState(null);
  const [loadingZip, setLoadingZip] = useState(false);
  const [zipError, setZipError] = useState(null);
  const [loadingLink, setLoadingLink] = useState(false);
  const [shareableLink, setShareableLink] = useState(null);

  const pdfFiles = [
    { name: "PDF Solicitud", file: "pdf_solicitud.pdf" },
    { name: "PDF Designación", file: "pdf_designacion.pdf" },
    { name: "PDF Rechazo", file: "pdf_rechazo.pdf" },
    { name: "PDF Aceptación", file: "pdf_aceptacion.pdf" },
    { name: "PDF Admisión", file: "pdf_admision.pdf" },
    { name: "PDF Actualización Acreencias", file: "pdf_actualizacion_acreencias.pdf" },
    { name: "PDF Revocación", file: "pdf_revocacion.pdf" },
    { name: "PDF Terminación", file: "pdf_terminacion.pdf" },
    { name: "PDF Cierre", file: "pdf_cierre.pdf" },
    { name: "PDF Reforma Acuerdo", file: "pdf_reforma_acuerdo.pdf" },
    { name: "PDF Incumplimiento", file: "pdf_incumplimiento.pdf" },
  ];

  // Simulated API functions - replace with your actual API calls
  const checkProcesoFileExists = async (fileName, procesoId) => {
    const fileExists = await BucketApi.checkProcesoFileExists(fileName, procesoId);
    return { data: { exists: fileExists.data.exists } };
  };

  const getProcesoFileUrlFresh = async (fileName, procesoId) => {
    const response = await BucketApi.getProcesoFileUrlFresh(fileName, procesoId);
    return response;
  };

  const getExistingZipUrl = async (procesoId) => {
    const response = await BucketApi.getProcesoZipDownloadUrl(procesoId);
    return response;
  };

  const getOrCreateZip = async (procesoId, forceRecreate = false) => {
    const response = await BucketApi.getOrCreateProcesoZip(procesoId, undefined, forceRecreate);
    return response;
  };

  useEffect(() => {
    const fetchPdfs = async () => {
      try {
        const pdfUrlsPromises = pdfFiles.map(async (pdf) => {
          try {
            const fileExists = await checkProcesoFileExists(pdf.file, proceso.identificador);
            if (!fileExists.data.exists) {
              return {
                ...pdf,
                url: null,
                errorType: "NO_SUCH_KEY",
                errorMessage: `El ${pdf.name} no existe en el bucket`,
              };
            } else {
              const response = await getProcesoFileUrlFresh(pdf.file, proceso.identificador);
              return { ...pdf, url: response.data.file_url };
            }
          } catch (error) {
            console.error(`Error completo al obtener ${pdf.file}:`, error);
            return {
              ...pdf,
              url: null,
              errorType: "OTHER_ERROR",
              errorMessage: `Error al obtener el archivo ${pdf.file}`,
            };
          }
        });
        const pdfData = await Promise.all(pdfUrlsPromises);
        setPdfUrls(pdfData);
      } catch (error) {
        console.error("Error obteniendo PDFs:", error);
      }
    };

    fetchPdfs();
  }, [proceso]);

  const handleGetShareableLink = async () => {
    setLoadingLink(true);
    setZipError(null);
    try {
      console.log("Obteniendo link de ZIP existente para proceso:", proceso.identificador);
      const response = await getExistingZipUrl(proceso.identificador);
      if (response.data.success) {
        const linkInfo = {
          downloadUrl: response.data.download_url,
          filename: response.data.zip_filename || `Proceso_${proceso.identificador}.zip`,
          fileCount: response.data.file_count || "varios",
          zipSize: response.data.zip_size || 0,
          expiresInDays: response.data.expires_in_days || 7,
          message: response.data.message,
          wasCreated: response.data.wasCreated || false,
        };
        setShareableLink(response.data.download_url);
        // Si no hay zipInfo, crear una versión básica
        if (!zipInfo) {
          setZipInfo({
            ...linkInfo,
            fileCount: "varios",
            zipSize: 0,
          });
        } else {
          // Actualizar zipInfo existente con nueva URL
          setZipInfo({
            ...zipInfo,
            downloadUrl: response.data.download_url,
          });
        }
        console.log("Link obtenido exitosamente:", response.data);
      } else {
        setZipError("No se encontró archivo ZIP existente. Debes crear uno primero.");
      }
    } catch (error) {
      console.error("Error obteniendo link:", error);
      setZipError("Error obteniendo link: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingLink(false);
    }
  };

  const handleSmartZipDownload = async (forceRecreate = false) => {
    setLoadingZip(true);
    setZipError(null);
    setZipInfo(null);
    setShareableLink(null);

    try {
      console.log("Iniciando descarga inteligente para proceso:", proceso.identificador);
      const response = await getOrCreateZip(proceso.identificador, forceRecreate);
      if (response.data.success) {
        const zipData = {
          downloadUrl: response.data.download_url,
          filename: response.data.zip_filename || `Proceso_${proceso.identificador}.zip`,
          fileCount: response.data.file_count || "varios",
          zipSize: response.data.zip_size || 0,
          expiresInDays: response.data.expires_in_days || 7,
          message: response.data.message,
          wasCreated: response.data.wasCreated,
        };

        setZipInfo(zipData);
        setShareableLink(response.data.download_url);

        const action = zipData.wasCreated ? "creado" : "encontrado";
        console.log(`ZIP ${action} exitosamente:`, response.data);
      } else {
        setZipError("Error al procesar archivo ZIP");
      }
    } catch (error) {
      console.error("Error en descarga inteligente:", error);
      setZipError("Error procesando ZIP: " + (error.response?.data?.error || error.message));
    } finally {
      setLoadingZip(false);
    }
  };

  const handleDownloadZip = () => {
    if (zipInfo?.downloadUrl) {
      window.open(zipInfo.downloadUrl, "_blank");
    }
  };

  const handleCopyLink = async () => {
    if (shareableLink) {
      try {
        await navigator.clipboard.writeText(shareableLink);
        alert("Link copiado al portapapeles");
      } catch (err) {
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = shareableLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        alert(obtenerMensajeError(err, "Link copiado al portapapeles"));
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0 || bytes === null || bytes === undefined) {
      return "Tamaño no disponible";
    }
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const availableFiles = pdfUrls.filter((pdf) => pdf.url && pdf.file !== "pdf_letra_cambio.pdf");
  const hasAvailableFiles = availableFiles.length > 0;

  return (
    <>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 1, md: 1 }}
        style={{
          marginBottom: "12px",
          padding: "16px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
        }}
      >
        <Grid size={12}>
          <h3 style={{ margin: "0 0 0 0", fontWeight: "bold" }}>
            Gestionar archivo ZIP del proceso
          </h3>
        </Grid>
        <Grid size={12}>
          {hasAvailableFiles ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <button
                    style={{
                      backgroundColor: loadingZip ? "#ccc" : "#2e7d32",
                      color: "white",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "4px",
                      cursor: loadingZip ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                    onClick={() => handleSmartZipDownload(false)}
                    disabled={loadingZip || loadingLink}
                  >
                    {loadingZip ? "Procesando..." : "Descargar ZIP"}
                  </button>

                  {/* Botón para obtener solo el link */}
                  <button
                    style={{
                      backgroundColor: loadingLink ? "#ccc" : "#1976d2",
                      color: "white",
                      border: "none",
                      padding: "12px 20px",
                      borderRadius: "4px",
                      cursor: loadingLink ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                    onClick={handleGetShareableLink}
                    disabled={loadingZip || loadingLink}
                  >
                    {loadingLink ? "Obteniendo..." : "Obtener Link"}
                  </button>

                  {/* Botón para recrear ZIP */}
                  <button
                    style={{
                      backgroundColor: loadingZip ? "#ccc" : "#f57c00",
                      color: "white",
                      border: "none",
                      padding: "12px 20px",
                      borderRadius: "4px",
                      cursor: loadingZip ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                    onClick={() => handleSmartZipDownload(true)}
                    disabled={loadingZip || loadingLink}
                  >
                    {loadingZip ? "Recreando..." : "Recrear ZIP"}
                  </button>
                </div>
              </div>
              {zipError && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    borderRadius: "4px",
                    border: "1px solid #ffcdd2",
                  }}
                >
                  <strong>Error:</strong> {zipError}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#fff3e0",
                color: "#f57c00",
                borderRadius: "4px",
                border: "1px solid #ffcc02",
              }}
            >
              No hay archivos disponibles para crear el ZIP
            </div>
          )}
          <hr style={{ margin: "8px 0", border: "none", borderTop: "2px solid #ddd" }} />
        </Grid>
        <Grid size={6}>
          {/* Información del ZIP y link compartible */}
          {(zipInfo || shareableLink) && (
            <>
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#e8f5e8",
                  borderRadius: "4px",
                  border: "2px solid #c8e6c9",
                }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <h4 style={{ margin: "0 0 2px 0", color: "#2e7d32" }}>
                    {zipInfo?.wasCreated === false
                      ? "ZIP Existente Encontrado"
                      : "ZIP Creado Exitosamente"}
                  </h4>
                  {zipInfo && (
                    <p style={{ margin: "0 0 2px 0", color: "#555", fontSize: "14px" }}>
                      {zipInfo.message} • Tamaño: {formatFileSize(zipInfo.zipSize)}
                    </p>
                  )}
                </div>

                {/* Botones de acción */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Botón de descarga */}
                  <button
                    style={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                    onClick={handleDownloadZip}
                  >
                    📥 Descargar ZIP
                  </button>

                  {/* Botón para copiar link */}
                  {shareableLink && (
                    <button
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                      onClick={handleCopyLink}
                    >
                      📋 Copiar Link
                    </button>
                  )}
                </div>
                {shareableLink && (
                  <div
                    style={{
                      padding: "12px",
                      backgroundColor: "#fff3e0",
                      borderRadius: "4px",
                      border: "1px solid #ffcc02",
                      marginBottom: "12px",
                    }}
                  >
                    <h5 style={{ margin: "0 0 2px 0", color: "#f57c00", fontSize: "14px" }}>
                      🔗 Link Compartible (Válido por {zipInfo?.expiresInDays || 7} días)
                    </h5>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        padding: "8px",
                        borderRadius: "3px",
                        border: "1px solid #e0e0e0",
                        fontSize: "12px",
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                        maxHeight: "70px",
                        overflow: "auto",
                      }}
                    >
                      {shareableLink}
                    </div>
                    <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#666" }}>
                      * Este enlace puede ser usado por {zipInfo?.expiresInDays || 7} días.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </Grid>
        {/* Panel lateral con lista de archivos */}
        <Grid size={6}>
          {hasAvailableFiles && (
            <>
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "4px",
                  border: "2px solid #90caf9",
                }}
              >
                <p style={{ fontWeight: "bold", margin: "0 0 8px 0", fontSize: "12px" }}>
                  📄 Archivos que se incluirán en el ZIP (Actas y Notificaciones):
                </p>
                <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "13px" }}>
                  {availableFiles.map((pdf) => (
                    <li key={pdf.file} style={{ marginBottom: "2px" }}>
                      <span>{pdf.name}</span>
                    </li>
                  ))}
                </ul>
                <p style={{ margin: "8px 0 0 0", fontSize: "11px", color: "#666" }}>
                  Total: {availableFiles.length} archivo{availableFiles.length !== 1 ? "s" : ""}
                </p>
              </div>
            </>
          )}
        </Grid>
      </Grid>

      {/* Grid para PDFs individuales */}
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 1, md: 1 }}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "16px",
        }}
      >
        <Grid size={12}></Grid>
      </Grid>

      {/* Sección de vista previa de documentos */}
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "16px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <h3 style={{ margin: "0 0 4px 0", fontWeight: "bold" }}>Vista Previa de Documentos</h3>
        <div>
          {pdfUrls.map((pdf, index) => (
            <div key={index} style={{ marginBottom: "20px" }}>
              <h4 style={{ margin: "0 0 2px 0", color: "#333" }}>{pdf.name}</h4>
              {pdf.url ? (
                <embed
                  src={pdf.url}
                  type="application/pdf"
                  width="100%"
                  height="250px"
                  style={{ border: "1px solid #ddd", borderRadius: "5px" }}
                />
              ) : (
                <div
                  style={{
                    padding: "12px",
                    border: "1px dashed #d32f2f",
                    borderRadius: "4px",
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  ❌ {pdf.errorMessage || "Archivo no disponible"}
                </div>
              )}
              {index < pdfUrls.length - 1 && (
                <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid #eee" }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

PDFsCard.propTypes = {
  proceso: PropTypes.object.isRequired,
};

export default PDFsCard;
